import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect } from 'react'
import useAxiosPrivateSAI from '../../../../hooks/useAxiosPrivateSAI'
import useAuth from '../../../../hooks/useAuth'

import EditClaimConfirmation from '../EditClaimConfirmation'
import StepOneEdit from '../Partials/StepOneEdit'
import StepTwoEdit from '../Partials/StepTwoEdit'
import StepThree from '../Partials/StepThree'
import moment from 'moment'

const EditClaim = ({ open, claim = {}, policy = {}, onRefresh, onClaimLoaded }) => {
  const axios = useAxiosPrivateSAI()
  const { createInfo } = useAuth()
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isFetchingClaimData, setIsFetchingClaimData] = useState(false)
  const [isStepOneValidState, setIsStepOneValidState] = useState(false)
  const [formData, setFormData] = useState({
    stepOne: {},
    stepTwo: {},
    stepThree: {},
  })
  const nextStep = () => setStep((prev) => Math.min(prev + 1, 4));

  const resetClaimEditor = () => {
    setStep(1)
    setFormData({
      stepOne: {},
      stepTwo: {},
      stepThree: {},
    });
    setIsStepOneValidState(false)
    setLoading(false)
  }

  useEffect(() => {
    if (!open) {
      resetClaimEditor()
    }
  }, [open])

  useEffect(() => {
    const fetchClaimData = async () => {
      if (!claim?.claim_id || !policy?.policy_id) return
      if (formData?.stepOne?.claim_id === claim.claim_id) return
      if (formData?.stepTwo?.claim_id === claim.claim_id) return

      setIsFetchingClaimData(true);
      try {
        const res = await axios.get(`/api/v1/staff-hub/claims/current/${policy.policy_id}/${claim.claim_id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`);
        const current = res.data;
        if (onClaimLoaded) onClaimLoaded(current.claim)
        const {
          claim_absence_start_date,
          claim_absence_end_date,
          claim_phased,
          shielding,
          confidential,
          claim_information,
          condition_id,
          condition,
          staff,
          declaration_signed,
          conditions
        } = current.claim;

        const parsedClaimInfo = JSON.parse(claim_information || '{}');

        setFormData({
          stepOne: {
            selectedStaff: staff || {},
            selectedCondition: {
              condition_id: conditions?.condition_id || condition_id,
              condition_title: conditions?.condition_title || condition,
              condition_phased: conditions?.condition_phased ?? 0,
            },
            absenceStart: claim_absence_start_date || null,
            absenceEnd: claim_absence_end_date || null,
            isPhasedReturn: !!claim_phased,
            isShielding: !!shielding,
            isConfidential: !!confidential,
            claim_id: current.claim.claim_id
          },
          stepTwo: {
            model: {
              continuous_absence: parsedClaimInfo.continuous_absence || null,
              twenty_working_days: parsedClaimInfo.twenty_working_days || null,
              parental_leave_planned: parsedClaimInfo.parental_leave_planned || null,
              upcoming_disciplinary_action: parsedClaimInfo.upcoming_disciplinary_action || null,
              supporting_documentation: parsedClaimInfo.supporting_documentation || null,
              relating_to: parsedClaimInfo.relating_to || null,
            },
          },
          stepThree: {
            accepted: !!declaration_signed,
          },
        })
      } catch (err) {
        console.error("Failed to load current claim data", err);
      } finally {
        setIsFetchingClaimData(false);
      }
    };

    fetchClaimData();
  }, [claim?.claim_id, policy?.policy_id]);

  useEffect(() => {
    const data = formData.stepOne;
    const isValid = checkStepOneValidity(data);
    setIsStepOneValidState(isValid);
  }, [
    formData.stepOne.selectedStaff,
    formData.stepOne.selectedCondition,
    formData.stepOne.isPhasedReturn,
    formData.stepOne.absenceStart,
    formData.stepOne.absenceEnd
  ]);

  const checkStepOneValidity = (data) => {
    if (!data || !data.selectedCondition || !data.absenceStart) return false;

    const { selectedStaff, selectedCondition, absenceStart, absenceEnd } = data;

    if (!selectedStaff.staff_id || !selectedCondition.condition_id) return false;
    if (!selectedStaff.stress && selectedCondition.stress === 0) return false;

    if (
      !selectedStaff.maternityCover &&
      selectedCondition.condition_id === 'e3afcdea-7b54-11e7-a963-336a7d328401'
    ) return false;

    const start = moment(absenceStart)
    const end = absenceEnd ? moment(absenceEnd) : null
    const today = moment()
    const futureClaimsAllowed = selectedCondition.future_claims === 1;

    if (start.isAfter(today) && !futureClaimsAllowed) return false;

    if (end) {
      if (end.isBefore(start)) return false;
      if (end.isAfter(today) && !futureClaimsAllowed) return false;
    }

    if (end) {
      if (end.isBefore(start)) return false
      if (end.isAfter(today) && selectedCondition.future_claims !== 1) return false
    }

    return true
  }

  const isStepTwoValid = () => {
    const model = formData.stepTwo?.model || {}
    return (
      model.continuous_absence &&
      model.twenty_working_days &&
      model.parental_leave_planned &&
      model.upcoming_disciplinary_action &&
      model.supporting_documentation
    )
  }

  const isStepThreeValid = () => {
    const data = formData.stepThree;
    return data?.accepted === true;
  }

  const isCurrentStepValid = () => {
    switch (step) {
      case 1:
        return isStepOneValidState
      case 2:
        return isStepTwoValid();
      case 3:
        return isStepThreeValid();
      default:
        return false;
    }
  }

  const handleNext = async () => {
    if (!isCurrentStepValid()) return

    setLoading(true)

    try {
      switch (step) {
        case 1:
          await submitStepOne()
          break;
        case 2:
          await submitStepTwo()
          break;
        case 3:
          await submitStepThree()
          break;
        default:
          break;
      }
    } finally {
      setLoading(false)
    }
  }

  const handleStepTwoUpdate = (stepTwoUpdate) => {
    setFormData(prev => ({
      ...prev,
      stepTwo: {
        ...prev.stepTwo,
        model: {
          ...prev.stepTwo.model,
          ...stepTwoUpdate.model,
        },
      },
    }))
  }

  const submitStepOne = async () => {
    const {
      selectedStaff,
      selectedCondition,
      absenceStart,
      absenceEnd,
      isPhasedReturn,
      isShielding,
      isConfidential,
    } = formData.stepOne;

    const claimId = formData.stepOne.claim_id || claim?.claim_id;

    if (!selectedStaff?.staff_id || !selectedCondition?.condition_id || !absenceStart) {
      createInfo('error', 'Missing required fields for Step One submission.')
      return;
    }

    const payload = {
      staff_id: selectedStaff.staff_id,
      claim_id: claimId || 0,
      condition_id: selectedCondition.condition_id,
      policy_id: policy.policy_id,
      claim_absence_start_date: absenceStart,
      claim_absence_end_date: absenceEnd || null,
      claim_phased: isPhasedReturn ? 1 : 0,
      shielding: isShielding ? 1 : 0,
      confidential: isConfidential ? 1 : 0
    }

    try {
      if (claim?.claim_id) {
        createInfo('success', 'Claim updated successfully.')
        nextStep()
      } else {
        const res = await axios.post('/api/claims', payload)
        if (res?.data?.claim_id) {
          const newClaim = res.data

          setFormData((prev) => ({
            ...prev,
            stepOne: {
              ...prev.stepOne,
              claim_id: newClaim.claim_id
            }
          }))

          createInfo('success', 'Claim created successfully.')
          nextStep();
        } else {
          createInfo('error', 'Claim created, but no claim id returned.')
        }
        nextStep();
      }
    } catch (err) {
      console.error("Step One error:", err)
      createInfo('error', 'Failed to submit Step One.')
    }
  }

  const submitStepTwo = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id
    const stepTwo = formData?.stepTwo

    if (!claimId || !stepTwo) {
      console.warn("Missing claim ID or step two data.")
      return
    }

    const {
      model = {}
    } = stepTwo;

    const {
      continuous_absence: continuous,
      twenty_working_days: contracted,
      parental_leave_planned: pregnant,
      upcoming_disciplinary_action: suspended,
      supporting_documentation: request,
      relating_to: relatingTo
    } = model;

    if (
      continuous == null ||
      contracted == null ||
      pregnant == null ||
      suspended == null ||
      request == null
    ) {
      console.warn("Incomplete Step Two data.")
      return
    }

    const claim_information = JSON.stringify(model)

    const payload = {
      claim_information,
      naht_member: "",
    }

    try {
      const res = await axios.post(`/api/v1/staff-hub/claims/update/${claimId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, payload)
      if (res.status === 200 || res.status === 201 || res.status === 204) {
        createInfo('success', 'Step Two successfully saved.')
        nextStep()
      } else {
        createInfo('error', 'Unexpected response while submitting Step Two.')
      }
    } catch (err) {
      createInfo('error', 'Failed to submit Step Two')
    }
  }

  const submitStepThree = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id

    if (!claimId) {
      createInfo('warning', 'No claim ID available to calculate the claim.')
      return
    }

    try {
      setSubmitting(true)
      const res = await axios.get(`/api/v1/staff-hub/claims/calculate/${claimId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`)
      if (res.status === 200) {
        createInfo('success', 'Claim successfully calculated.')
        nextStep()
        if (onRefresh) onRefresh()
      } else {
        createInfo('error', 'Unexpected response from calculate endpoint.')
      }
    } catch (err) {
      createInfo('error', 'Failed to calculate claim')
    }
    setSubmitting(false)
  }

  return (
    <div className="p-6">
      {step >= 1 && step <= 3 && (
        <>
          {isFetchingClaimData && (
            <div className="mb-4 inline-flex items-center gap-2 text-sm text-sas">
              <span>Loading...</span>
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )}
          {step === 1 && (
            <StepOneEdit
              claim={claim}
              policyId={policy?.policy_id}
              data={formData.stepOne}
              onUpdate={(updated) =>
                setFormData((prev) => ({
                  ...prev,
                  stepOne: { ...prev.stepOne, ...updated },
                }))
              }
            />
          )}
          {step === 2 && (
            <StepTwoEdit
              key={claim?.claim_id}
              claim={claim}
              policy={policy}
              data={formData.stepTwo}
              conditionId={claim?.condition_id}
              onUpdate={handleStepTwoUpdate}
            />
          )}
          {step === 3 && (
            <StepThree
              claim={claim}
              data={formData.stepThree}
              onUpdate={(data) =>
                setFormData((prev) => ({ ...prev, stepThree: data }))
              }
            />
          )}
          <div className="flex justify-between mt-8">
            {step < 3 ? (
              loading ? (
                <button
                  disabled
                  className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  Loading...
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid() || loading || submitting || isFetchingClaimData}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${(isCurrentStepValid() && !loading && !submitting && !isFetchingClaimData) ? 'bg-sas hover:bg-sas-dark' : 'bg-gray-400 cursor-not-allowed'
                    }`}
                >
                  Next Step
                </button>
              )
            ) : (
              <>
                {formData.stepThree?.accepted && (
                  <button onClick={submitStepThree} disabled={submitting || isFetchingClaimData} className={`px-4 py-2 text-sm font-medium text-white rounded-md ${(submitting || isFetchingClaimData) ? 'bg-gray-400 cursor-not-allowed' : 'bg-sas hover:bg-sas-dark'}`}>
                    {submitting ? (
                      <span className="inline-flex items-center gap-2">
                        Loading...
                        <FontAwesomeIcon icon={faSpinner} spin />
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </>
      )}
      {step === 4 && <EditClaimConfirmation />}
    </div>
  )
}

export default EditClaim
